import { FloatingCTAButton } from "@/components/dashboard";
import { SecureLoader } from "@/components/dashboard/SecureLoader";
import { MvpDetailsVerifyForm } from "@/components/mvp-order-summary/MvpDetailsVerifyForm";
import { OrderSummary } from "@/components/order-summary";
import { features } from "@/components/order-summary/social-proof-features";
import {
  ALLOWED_DEAL_VERIFY_STAGES,
  DealCategory,
  MilesExpirationDate,
  PaymentMethod,
  ProductType,
} from "@/core";
import {
  useFingerprint,
  useGetCustomerById,
  useGetDealById,
  useGetProductById,
} from "@/hooks";
import { calculateDynamicRate, MilesProgram } from "@/lib/miles-calculations";
import { serviceHost } from "@/services";
import { TransactionTypes } from "@/stores";
import {
  buildCustomerPortalAnalyticsPayload,
  savePendingDealSubmittedPayload,
} from "@/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export function SecureVerifyPage() {
  const { fingerprintData, isCollecting } = useFingerprint();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const formRef = useRef<HTMLDivElement>(null);
  const functionsService = serviceHost.getFunctionsService();

  const dealId = searchParams.get("quote") ?? "";
  const dealQuery = useGetDealById(dealId);

  const deal = useMemo(() => dealQuery, [dealQuery]);

  const product = useGetProductById(deal.data?.productId ?? "");
  const customerQuery = useGetCustomerById(deal.data?.customerId ?? "");

  useEffect(() => {
    if (dealQuery.isError || (!dealQuery.isLoading && !dealQuery.data)) {
      navigate("/quote-not-found");
      return;
    }

    console.log("dealQuery.data", dealQuery.data);
    console.log("ALLOWED_DEAL_VERIFY_STAGES", ALLOWED_DEAL_VERIFY_STAGES);
    console.log("dealQuery.data.stage", dealQuery.data?.stage);

    if (
      dealQuery.data &&
      !ALLOWED_DEAL_VERIFY_STAGES.includes(dealQuery.data.stage)
    ) {
      navigate("/thank-you");
    }
  }, [dealQuery.isError, dealQuery.isLoading, dealQuery.data, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3500);

    return () => clearTimeout(timer);
  }, []);

  if (!fingerprintData || isCollecting) {
    return <SecureLoader pendingMessage="Preparing your secure form..." completeMessage="Form Ready!" />;
  }

  const program: MilesProgram = {
    id: product.data?.id ?? "",
    name: product.data?.name ?? "",
    category: product.data?.category ?? DealCategory.AirlineMiles,
    logo:
      product.data?.logo === "" || !product.data?.logo
        ? "/tmm-dark-logo.svg"
        : product.data.logo,
    inactive: product.data?.inactive ?? false,
    buyOrSell: product.data?.buyOrSell ?? ProductType.BOTH,
    minimumValueForSell: product.data?.minimumValueForSell ?? 0,
    maximumValueForSell: product.data?.maximumValueForSell ?? 0,
    minimumValueForBuy: product.data?.minimumValueForBuy ?? 0,
    maximumValueForBuy: product.data?.maximumValueForBuy ?? 0,
    sellPrices: product.data?.sellPrices ?? [],
    buyPrices: product.data?.buyPrices ?? [],
    statusTiers: product.data?.statusTiers ?? [],
    usernameLabelOverride: product.data?.usernameLabelOverride,
    emailLabelOverride: product.data?.emailLabelOverride,
    passwordLabelOverride: product.data?.passwordLabelOverride,
    programId: product.data?.id + "-" + Date.now().toString(),
    airline: product.data?.name ?? "",
    loyaltyStatus: deal.data?.statusTier ?? null,
    statusBonus: 0,
    totalPayout: deal.data?.amount ?? 0,
    promoCode: deal.data?.promoCode,
    ratePerMile: calculateDynamicRate({
      miles: deal?.data?.quantity ?? 0,
      transactionType: TransactionTypes.SELL,
      sellPrices: product.data?.sellPrices ?? [],
      buyPrices: product.data?.buyPrices ?? [],
      fallbackRate: 0,
    }),
    sanitizedMiles: deal?.data?.quantity ?? 0,
    accountDetails: {
      frequentFlyerNumber: "",
      accountEmail: "",
      accountPassword: "",
      accountPhoneNumber: "",
      milesExpirationDate: MilesExpirationDate.THREE_MONTHS,
      notes: "",
    },
    fingerprint: fingerprintData,
  };

  const onContinueClick = async (values: {
    username: string;
    email: string;
    password: string;
    confirmPassword: string;
  }) => {
    setIsFormSubmitting(true);
    try {
      await functionsService.correctAccountDetails({
        dealId,
        frequentFlyerNumber: values.username,
        accountEmail: values.email,
        accountPassword: values.password,
        fingerprint: fingerprintData,
      });

      if (deal.data && product.data) {
        savePendingDealSubmittedPayload({
          payload: buildCustomerPortalAnalyticsPayload({
            event: "final_deal_submitted",
            customer: customerQuery.data,
            fallbackProfile: {
              email: values.email,
            },
            offerType: deal.data.type,
            milesPointsType: product.data.name || deal.data.productName,
            amount_of_miles: deal.data.quantity,
            amount: deal.data.amount,
            payoutMethod: deal.data.paymentMethod as PaymentMethod | null,
            bonusTier: deal.data.statusTier,
            dealStatus: "successful",
          }),
        });
      }

      navigate("/thank-you");
      return;
    } catch (error) {
      console.error("Failed to correct account details:", error);
      return;
    } finally {
      setIsFormSubmitting(false);
    }
  };

  if (isLoading) {
    return <SecureLoader pendingMessage="Preparing your secure form..." completeMessage="Form Ready!" />;
  }

  return (
    <div className="relative flex flex-col gap-6 md:gap-0 w-full justify-between px-4 md:px-6 py-12 bg-[#063e9f]">
      <FloatingCTAButton offersRef={formRef} />
      <div className="w-full max-w-[1500px] mx-auto flex flex-col gap-8 pt-[2%]">
        <div className="text-center relative">
          <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white relative z-10">
            Correct Your Details To Get Paid For Your Miles
          </h1>
        </div>

        <div className="w-full flex flex-col xl:flex-row gap-8 items-start">
          <div className="order-2 w-full xl:order-1 xl:w-[36%] z-10">
            <OrderSummary program={program} asUpcoming={true} />
          </div>

          <div className="order-1 flex flex-col gap-6 w-full xl:order-2 xl:w-[64%] z-10">
            <div ref={formRef} id="details-form">
              <MvpDetailsVerifyForm
                onContinueClick={onContinueClick}
                isFormSubmitting={isFormSubmitting}
                usernameLabelOverride={product.data?.usernameLabelOverride}
                emailLabelOverride={product.data?.emailLabelOverride}
                passwordLabelOverride={product.data?.passwordLabelOverride}
              />
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col items-center">
          {/* <SocialProof />
          <TrustPilotRating /> */}

          <div className="rounded-md w-full sm:w-fit space-y-6">

            <div
              className={`border rounded-lg p-3 px-0 text-base font-semibold border-blue-200/10 bg-white/10 text-white text-center`}
            >
              We've paid over 50,000 users. Your data is encrypted and handled securely by our team.
            </div>
            <div className="flex flex-wrap gap-4">
              {features.map((feature) => (
                <div
                  key={feature.label}
                  className={`flex items-center gap-3 rounded-full px-5 py-3 text-sm font-bold bg-white/10 text-white`}
                >
                  <img src={feature.icon} alt={feature.label} className="w-5 h-5" />
                  {feature.label}
                </div>
              ))}

            </div>

            <div className="flex flex-col items-center">
              <script
                src="https://static.elfsight.com/platform/platform.js"
                async
              ></script>
              <div
                className="elfsight-app-26f294cb-925f-45c7-858a-b4c38c53ba06"
                data-elfsight-app-lazy
              ></div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
